// Полный экран: игра занимает весь экран вместо окна браузера.
//
// Просит его вся страница, а не canvas. Попапы, кнопки и сообщение об ошибке лежат в том же
// документе, и уйди на полный экран один canvas, они остались бы за его краем. Новый размер
// игре переставит Pixi: он следит за окном сам, см. main.ts.
//
// Старый Safari знает те же вызовы под своими именами. Игра зовёт стандартные, а к именам
// Safari сходит только там, где стандартных нет.

/** Имена Safari. Игра сходит к ним, когда стандартного вызова в браузере нет. */
type SafariDocument = Document & {
  readonly webkitFullscreenEnabled?: boolean;
  readonly webkitFullscreenElement?: Element | null;
  readonly webkitExitFullscreen?: () => void;
};

/** Имя Safari у запроса полного экрана. */
type SafariElement = HTMLElement & {
  readonly webkitRequestFullscreen?: () => void;
};

function page(): SafariDocument {
  return document as SafariDocument;
}

/**
 * Даёт ли браузер полный экран.
 *
 * Не даёт его iPhone: там полного экрана нет ни у одной страницы. Кнопке в этом случае
 * на экране делать нечего, см. ui/fullscreenButton.ts.
 */
export function fullscreenAvailable(): boolean {
  const doc: SafariDocument = page();

  return doc.fullscreenEnabled || doc.webkitFullscreenEnabled === true;
}

/** Занимает ли игра сейчас весь экран. */
export function fullscreenOn(): boolean {
  const doc: SafariDocument = page();

  return (doc.fullscreenElement ?? doc.webkitFullscreenElement ?? null) !== null;
}

/**
 * Переключает полный экран.
 *
 * Просить его браузер даёт только в ответ на действие игрока, поэтому зовут это прямо
 * из нажатия кнопки. Отказ игра сносит молча: сказать о нём ей нечем, а игра от отказа
 * не ломается.
 */
export function toggleFullscreen(): void {
  const doc: SafariDocument = page();

  if (fullscreenOn()) {
    if (doc.exitFullscreen !== undefined)
      void doc.exitFullscreen().catch((): void => {});
    else
      doc.webkitExitFullscreen?.();

    return;
  }

  const root: SafariElement = doc.documentElement as SafariElement;

  if (root.requestFullscreen !== undefined)
    void root.requestFullscreen().catch((): void => {});
  else
    root.webkitRequestFullscreen?.();
}
